import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';
import userService from '../../services/UserService.js';
import salaryService from '../../services/SalaryService.js';
import attendanceService from '../../services/AttendanceService.js';
import AlertModal from '../common/AlertModal.js';
import Alert from '../common/Alert.js';
import { Bell, LogOut, Menu, Search, Settings } from 'lucide-react';
import UserAvatar from './UserAvatar.js';
import { useSidebarMobileOptional } from '../../context/SidebarMobileContext.js';
import Button from './Button.js';
import LanguageSwitcher from './LanguageSwitcher.js';
import { hasAttendanceAccess, canManualAttendance } from '../../utils/networkMode.js';
import AppBrandLogo from './AppBrandLogo.js';
import { useCompanyLogo } from '../../hooks/useCompanyLogo.js';
import { getCurrentDevicePosition } from '../../utils/deviceLocation.js';
import dqShortLogo from '../../static/images/dq_logo.png';
import HeaderShipmentSearch from './HeaderShipmentSearch.js';
import ShipmentDetailModal from '../shipment/ShipmentDetailModal.js';
import alertService from '../../services/AlertService.js';

const Header = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();
  const sidebarMobile = useSidebarMobileOptional();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [fullUserData, setFullUserData] = useState(null);
  const [profileError, setProfileError] = useState('');
  const [currentSalary, setCurrentSalary] = useState(null);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [headerProfilePhoto, setHeaderProfilePhoto] = useState(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const companyLogo = useCompanyLogo(user);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [workDuration, setWorkDuration] = useState(0);
  const [loginTime, setLoginTime] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [headerShipmentId, setHeaderShipmentId] = useState(null);
  const canSearchShipments = ['Admin', 'Accountant', 'Agent'].includes(user?.role);

  useEffect(() => {
    if (showProfileModal && user?.user_id && !fullUserData) {
      fetchFullUserData();
      fetchCurrentSalary();
    }
  }, [showProfileModal, user?.user_id]);

  useEffect(() => {
    if (user?.user_id && !headerProfilePhoto) {
      userService.getById(user.user_id).then((response) => {
        setHeaderProfilePhoto(response.data?.profile_photo_url || null);
      }).catch(() => {});
    }
  }, [user?.user_id]);

  useEffect(() => {
    if (user?.user_id && hasAttendanceAccess(user)) {
      checkActiveAttendance();
    }
  }, [user?.user_id, user?.network_mode]);

  useEffect(() => {
    if (!user?.user_id) return undefined;
    const load = () => {
      alertService.getUnreadCount(user.user_id)
        .then((res) => setUnreadCount(res?.unread_count ?? 0))
        .catch(() => {});
    };
    load();
    window.addEventListener('rt-alerts-refresh', load);
    return () => window.removeEventListener('rt-alerts-refresh', load);
  }, [user?.user_id]);

  useEffect(() => {
    let interval;
    if (isLoggedIn && loginTime) {
      interval = setInterval(() => {
        const now = new Date();
        const login = new Date(loginTime);
        const duration = Math.floor((now - login) / 1000);
        setWorkDuration(duration);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLoggedIn, loginTime]);

  const handleAlertClick = () => setShowAlertModal(true);

  const handleCloseAlertModal = () => {
    setShowAlertModal(false);
  };

  const getCurrentPosition = () => getCurrentDevicePosition();

  const checkActiveAttendance = async () => {
    try {
      const response = await attendanceService.getActiveStatus();
      if (response.is_active && response.data) {
        setIsLoggedIn(true);
        setLoginTime(response.data.login_time);
        setAttendanceDate(response.data.attendance_date);
      } else {
        setIsLoggedIn(false);
        setLoginTime(null);
        setAttendanceDate(null);
        setWorkDuration(0);
      }
    } catch (err) {
      console.error('Failed to check attendance status:', err);
    }
  };

  const handleAttendanceLogin = async () => {
    try {
      setAttendanceLoading(true);
      Alert.info('Getting your location...');
      const position = await getCurrentPosition();
      const response = await attendanceService.loginAttendance(position.latitude, position.longitude);
      setIsLoggedIn(true);
      setLoginTime(response.data.login_time);
      setAttendanceDate(response.data.attendance_date);
      setWorkDuration(0);
      Alert.success('Attendance logged in successfully!');
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to login attendance';
      Alert.error(errorMessage);
      if (err.response?.status === 400 && err.response?.data?.active_attendance) {
        checkActiveAttendance();
      }
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleAttendanceLogout = async () => {
    const today = new Date().toISOString().split('T')[0];
    const loginDate = attendanceDate ? new Date(attendanceDate).toISOString().split('T')[0] : today;

    let confirmMessage = `You have worked for ${formatDuration(workDuration)}. Are you sure you want to logout?`;

    if (loginDate !== today) {
      const loginDateFormatted = new Date(attendanceDate).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
      });
      confirmMessage = `You logged in on ${loginDateFormatted}. You have worked for ${formatDuration(workDuration)}. Are you sure you want to logout now?`;
    }

    const confirmed = await Alert.confirm(confirmMessage, 'Logout Attendance');

    if (confirmed) {
      try {
        setAttendanceLoading(true);
        Alert.info('Getting your location...');
        const position = await getCurrentPosition();
        const response = await attendanceService.logoutAttendance(position.latitude, position.longitude);
        setIsLoggedIn(false);
        setLoginTime(null);
        setAttendanceDate(null);
        setWorkDuration(0);
        Alert.success(`Logged out successfully! Total work time: ${response.data.work_hours_formatted}`);
      } catch (err) {
        Alert.error(err.response?.data?.error || err.message || 'Failed to logout attendance');
      } finally {
        setAttendanceLoading(false);
      }
    }
  };

  const getLoginDisplayDate = () => {
    if (!attendanceDate) return '';
    const today = new Date().toISOString().split('T')[0];
    const loginDate = new Date(attendanceDate).toISOString().split('T')[0];
    if (loginDate !== today) {
      return new Date(attendanceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    }
    return '';
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const formatDurationShort = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const fetchFullUserData = async () => {
    try {
      setLoadingUserData(true);
      setProfileError('');
      const response = await userService.getById(user.user_id);
      const record = response?.data || response;
      if (!record || typeof record !== 'object' || !record.user_id) {
        setProfileError('Could not load the full profile.');
        return;
      }
      setFullUserData(record);
    } catch (err) {
      console.error('Failed to load user details:', err);
      setProfileError(err.response?.data?.error || 'Could not load the full profile.');
    } finally {
      setLoadingUserData(false);
    }
  };

  const fetchCurrentSalary = async () => {
    try {
      const currentDate = new Date();
      const currentMonth = currentDate.toLocaleString('en-US', { month: 'long' }).toLowerCase();
      const currentYear = currentDate.getFullYear();
      const response = await salaryService.getAll(1, 10, {
        user_id: user.user_id, month: currentMonth, year: currentYear
      });
      if (response.data && response.data.length > 0) {
        setCurrentSalary(response.data[0]);
      }
    } catch (err) {
      console.error('Failed to load salary data:', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const getProfileImageUrl = (userData = null) => {
    const target = userData || user;
    return target?.profile_photo_url || null;
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getSalaryStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-700',
      credited: 'bg-green-100 text-green-700',
      processing: 'bg-blue-100 text-blue-700',
      hold: 'bg-orange-100 text-orange-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-700';
  };

  const showAttendanceClockIn = canManualAttendance(user);
  const showAttendanceControls = showAttendanceClockIn || (isLoggedIn && hasAttendanceAccess(user));

  return (
    <>
      <header className="bg-white sticky top-0 z-50 isolate">
        <div className="flex items-center justify-between gap-1.5 sm:gap-3 px-2 sm:px-6 py-2 sm:py-2.5 min-h-[52px] sm:min-h-[57px]">
          {/* Left: menu (mobile) + brand/company logo — never collapse on mobile */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 min-w-0 sm:flex-1 sm:overflow-hidden">
            {user?.role !== 'Staff' && sidebarMobile && !sidebarMobile.isOpen && (
              <Button
                variant="ghost"
                onClick={sidebarMobile.openSidebar}
                className="lg:hidden !p-1.5 sm:!p-2 -ml-0.5 flex-shrink-0 rounded-lg bg-gray-100 hover:bg-gray-200 active:bg-gray-300"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5 text-gray-700" />
              </Button>
            )}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="flex-shrink-0 cursor-pointer bg-transparent border-0 p-0"
              title="Refresh"
              aria-label="Refresh page"
            >
              <AppBrandLogo
                companyLogo={companyLogo}
                variant="header"
                className="h-9 sm:h-10 w-auto max-h-9 sm:max-h-10 max-w-[96px] sm:max-w-[160px] md:max-w-[200px] object-contain flex-shrink-0"
              />
            </button>
          </div>
          {/* Right: search + actions + DQ short */}
          <div className="flex items-center gap-0.5 sm:gap-1.5 flex-shrink-0 ml-auto">
            {loading ? (
              <div className="w-7 h-7 sm:w-8 sm:h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            ) : user ? (
              <>
                {canSearchShipments && (
                  <div className="hidden md:block">
                    <HeaderShipmentSearch onSelectShipment={setHeaderShipmentId} />
                  </div>
                )}
                {canSearchShipments && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setMobileSearchOpen((open) => !open)}
                    className="md:hidden relative !p-1.5 sm:!p-2 rounded-lg"
                    title={t('dashboard.searchGcOrMobile')}
                    aria-label={t('dashboard.searchGcOrMobile')}
                    aria-expanded={mobileSearchOpen}
                  >
                    <Search className="w-[18px] h-[18px] sm:w-5 sm:h-5 text-gray-600" />
                  </Button>
                )}
                {/* Alert bell */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAlertClick}
                  className="relative !p-1.5 sm:!p-2 rounded-lg"
                  title={t('header.notifications')}
                >
                  <Bell className="w-[18px] h-[18px] sm:w-5 sm:h-5 text-gray-600" />
                  {unreadCount > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-semibold text-white bg-red-500 rounded-full ring-2 ring-white leading-none"
                      aria-label={t('header.unreadNotifications')}
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Button>

                {/* Attendance section */}
                {showAttendanceControls && (
                  <div className="flex items-center gap-0.5 sm:gap-2 sm:border-l sm:border-gray-200 sm:pl-2.5">
                    {isLoggedIn ? (
                      <div className="flex items-center gap-0.5 sm:gap-2">
                        <div className="relative flex-shrink-0 hidden sm:block">
                          <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                          <div className="absolute inset-0 w-2.5 h-2.5 bg-green-500 rounded-full opacity-75 animate-ping" />
                        </div>
                        <div className="hidden sm:flex flex-col items-start">
                          <span className="text-xs font-mono text-gray-900 font-semibold">
                            {formatDuration(workDuration)}
                          </span>
                          {getLoginDisplayDate() && (
                            <span className="text-[10px] text-gray-500">{getLoginDisplayDate()}</span>
                          )}
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={handleAttendanceLogout}
                          disabled={attendanceLoading}
                          className="rounded-md text-xs !px-1.5 sm:!px-2 !py-1"
                          title={`${t('header.logoutAttendance')} (${formatDuration(workDuration)})`}
                        >
                          {attendanceLoading ? '...' : (
                            <>
                              <span className="sm:hidden">{formatDurationShort(workDuration)}</span>
                              <span className="hidden sm:inline">{t('header.clockOut')}</span>
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleAttendanceLogin}
                        disabled={attendanceLoading}
                        className="rounded-md text-xs !p-1.5 sm:!px-2.5 sm:!py-1.5"
                        title={t('header.loginAttendance')}
                      >
                        {attendanceLoading ? '...' : (
                          <>
                            <div className="w-2 h-2 bg-white rounded-full flex-shrink-0" />
                            <span className="hidden sm:inline">{t('header.clockIn')}</span>
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                )}

                {/* Profile */}
                <Button
                  variant="ghost"
                  onClick={() => setShowProfileModal(true)}
                  className="hover:bg-gray-50 rounded-lg !p-1 sm:!p-1.5 sm:border-l sm:border-gray-200 sm:pl-2.5 sm:ml-0.5"
                >
                  <UserAvatar
                    src={headerProfilePhoto || getProfileImageUrl()}
                    alt={user.name}
                    size="sm"
                    className="border-2 border-gray-200"
                  />
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-semibold text-gray-800 leading-tight">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.role}</p>
                  </div>
                </Button>

                {/* System logout */}
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="!p-1.5 sm:!p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg flex-shrink-0"
                  title={t('header.logout')}
                >
                  <LogOut className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
                </Button>
              </>
            ) : null}

            {/* DQ short brand — desktop web only; mobile/native uses dq_logo.png on the left */}
            <img
              src={dqShortLogo}
              alt="DQ"
              className="hidden sm:block h-7 sm:h-9 w-auto max-h-7 sm:max-h-9 object-contain flex-shrink-0"
              decoding="async"
            />
          </div>
        </div>
        {canSearchShipments && mobileSearchOpen && (
          <div className="md:hidden px-2 pb-2">
            <HeaderShipmentSearch
              variant="mobile"
              onSelectShipment={(id) => {
                setHeaderShipmentId(id);
                setMobileSearchOpen(false);
              }}
            />
          </div>
        )}
      </header>

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
          <div className="bg-white w-full sm:max-w-4xl sm:rounded-xl shadow-2xl min-h-screen sm:min-h-0 sm:my-8 max-h-screen sm:max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">{t('common.profile')}</h2>
              <div className="flex items-center gap-1">
                <LanguageSwitcher variant="compact" />
                <Button
                  variant="ghost"
                  onClick={() => setShowProfileModal(false)}
                  className="p-2 rounded-full"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </Button>
              </div>
            </div>

            {(() => {
              const profileUser = fullUserData || (user ? {
                name: user.name,
                email: user.email,
                username: user.username,
                role_name: user.role,
                company_name: user.company_name,
                company_id: user.company_id,
                user_id: user.user_id,
                contact: user.contact,
                city: user.city,
                location: user.location,
                status: user.status || 'active',
                profile_photo_url: headerProfilePhoto || user.profile_photo_url,
              } : null);
              if (loadingUserData && !profileUser) {
                return (
                  <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4" />
                      <p className="text-gray-500 text-sm">{t('common.loading')}</p>
                    </div>
                  </div>
                );
              }
              if (!profileUser) return null;
              return (
              <>
                {profileError ? (
                  <p className="px-4 sm:px-8 pt-4 text-sm text-red-600">{profileError}</p>
                ) : null}
                {/* Profile Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-8">
                  <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                    <UserAvatar
                      src={getProfileImageUrl(profileUser)}
                      alt={profileUser.name}
                      size="xl"
                      className="border-4 border-white/30 bg-white"
                      iconClassName="text-gray-400"
                    />
                    <div className="flex-1 text-center sm:text-left">
                      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">{profileUser.name}</h2>
                      <p className="text-blue-200 text-sm sm:text-base mb-3">{profileUser.role_name}</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        profileUser.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {capitalizeStatus(profileUser.status)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className="p-4 sm:p-8 space-y-6">
                  {currentSalary && (
                    <ProfileSection title={`Current Salary Status (${currentSalary.month_display} ${currentSalary.year})`}>
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-6 rounded-xl border border-blue-100">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Net Salary</label>
                            <p className="text-lg font-bold text-blue-600">{formatCurrency(currentSalary.net_salary)}</p>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Payment Status</label>
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getSalaryStatusBadge(currentSalary.status)}`}>
                              {String(currentSalary.status || '').toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Payment Date</label>
                            <p className="text-sm text-gray-800 font-medium">{formatDate(currentSalary.payment_date)}</p>
                          </div>
                        </div>
                      </div>
                    </ProfileSection>
                  )}

                  <ProfileSection title={t('common.basicInfo')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <ProfileField label="Username" value={profileUser.username || 'N/A'} />
                      <ProfileField label="Full Name" value={profileUser.name} />
                      <ProfileField label="Email" value={profileUser.email} />
                      <ProfileField label="Company" value={profileUser.company_name ? `${profileUser.company_name} (${profileUser.company_id || 'N/A'})` : 'N/A'} />
                      <ProfileField label="Date of Birth" value={formatDate(profileUser.dob)} />
                    </div>
                  </ProfileSection>

                  <ProfileSection title={t('common.contactInfo')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <ProfileField label="Contact Number" value={profileUser.contact} />
                      <ProfileField label="City" value={profileUser.city} />
                      <div className="sm:col-span-2">
                        <ProfileField label="Location/Address" value={profileUser.location} />
                      </div>
                    </div>
                  </ProfileSection>

                  <ProfileSection title={t('common.documentInfo')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <ProfileField label="Document Type" value={profileUser.document_type ? profileUser.document_type.replace(/_/g, ' ').toUpperCase() : 'N/A'} />
                      <ProfileField label="Document Number" value={profileUser.document_number || 'N/A'} />
                    </div>
                  </ProfileSection>

                  <ProfileSection title={t('common.roleAndStatus')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <ProfileField label="Role" value={profileUser.role_name} />
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          profileUser.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {capitalizeStatus(profileUser.status)}
                        </span>
                      </div>
                    </div>
                  </ProfileSection>

                  <ProfileSection title={t('common.systemInfo')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <ProfileField label="User ID" value={profileUser.user_id} />
                      <ProfileField label="Created By" value={profileUser.created_by || 'N/A'} />
                      <ProfileField label="Created At" value={formatDate(profileUser.created_at)} />
                      <ProfileField label="Last Updated" value={formatDate(profileUser.updated_at)} />
                    </div>
                  </ProfileSection>
                </div>

                {/* Modal Footer */}
                <div className="sticky bottom-0 bg-gray-50 px-4 sm:px-8 py-4 border-t flex flex-col sm:flex-row gap-3">
                  {['Admin', 'System Admin', 'Owner'].includes(user?.role) && (
                    <Button
                      variant="primary"
                      fullWidth
                      onClick={() => {
                        setShowProfileModal(false);
                        navigate('/company-settings');
                      }}
                      className="rounded-lg flex items-center justify-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Company Settings
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={() => setShowProfileModal(false)}
                    className="rounded-lg"
                  >
                    {t('common.close')}
                  </Button>
                </div>
              </>
              );
            })()}
          </div>
        </div>
      )}

      <AlertModal
        isOpen={showAlertModal}
        onClose={handleCloseAlertModal}
        userId={user?.user_id}
        onUnreadChange={setUnreadCount}
      />
      <ShipmentDetailModal
        shipmentId={headerShipmentId}
        onClose={() => setHeaderShipmentId(null)}
      />
    </>
  );
};

function ProfileSection({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b-2 border-blue-500">
        {title}
      </h3>
      {children}
    </div>
  );
}

function ProfileField({ label, value }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <p className="text-sm text-gray-800 font-medium break-all">{value}</p>
    </div>
  );
}

export default Header;
